/**
 * Mitigación para CVE-2025-69873: ajv ReDoS vulnerability with $data option
 * 
 * Este archivo implementa medidas de seguridad para mitigar la vulnerabilidad
 * CVE-2025-69873 mientras se mantiene la compatibilidad del proyecto.
 * 
 * VULNERABILIDAD: ajv@6.12.6 sufre de ReDoS con la opción $data habilitada
 * SOLUCIÓN TEMPORAL: Deshabilitar $data option en validaciones de schema
 */

// Configuración segura para ajv cuando se use directamente en el proyecto
const AJV_SECURITY_CONFIG = {
    // Deshabilitar $data para prevenir ReDoS
    $data: false,
    
    // Configuraciones adicionales de seguridad
    strict: true,
    strictSchema: true,
    strictNumbers: true,
    strictTypes: 'log',
    strictTuples: 'log',
    strictRequired: 'log',
    
    // Límites para prevenir abuso
    meta: false,
    validateFormats: false,
    addUsedSchema: false,
    
    // Configuración de tiempo límite para regex
    timeout: 5000, // 5 segundos máximo para validación
    
    // Prevenir eval code generation
    code: {
        optimize: false,
        source: false
    }
};

/**
 * Wrapper seguro para instancias de ajv
 * @param {object} options - Opciones adicionales para ajv
 * @returns {object} - Instancia de ajv configurada de forma segura
 */
function createSecureAjv(options = {}) {
    // Verificar si ajv está disponible
    let Ajv;
    try {
        Ajv = require('ajv');
    } catch (error) {
        console.warn('AJV no está disponible:', error.message);
        return null;
    }
    
    // Combinar configuración segura con opciones del usuario
    const config = {
        ...AJV_SECURITY_CONFIG,
        ...options,
        // Forzar que $data siempre sea false por seguridad
        $data: false
    };
    
    console.log('🛡️ Creando instancia AJV con configuración de seguridad para CVE-2025-69873');
    
    return new Ajv(config);
}

/**
 * Valida un schema antes de usarlo para detectar patrones peligrosos
 * @param {object} schema - Schema a validar
 * @returns {object} - {isValid: boolean, warnings: string[]}
 */
function validateSchemaForSecurity(schema) {
    const warnings = [];
    
    // Verificar uso de $data
    const schemaString = JSON.stringify(schema);
    if (schemaString.includes('$data')) {
        warnings.push('SECURITY WARNING: Schema contiene referencias $data que pueden causar ReDoS (CVE-2025-69873)');
    }
    
    // Verificar patrones regex potencialmente peligrosos
    const dangerousPatterns = [
        /\(\.\*\)\+/g,      // (.*)+
        /\(\[.\*\]\)\*/g,   // ([.*])*
        /\(\?\=.*\)\+/g,    // (?=.*)+
        /\(\w\|\w\)\*/g,    // (a|a)*
        /\(\.\+\)\{/g       // (.+){
    ];
    
    dangerousPatterns.forEach((pattern, index) => {
        if (pattern.test(schemaString)) {
            warnings.push(`SECURITY WARNING: Schema contiene patrón regex peligroso #${index + 1} que puede causar ReDoS`);
        }
    });
    
    return {
        isValid: warnings.length === 0,
        warnings: warnings
    };
}

/**
 * Sanitiza un schema removiendo elementos peligrosos
 * @param {object} schema - Schema original
 * @returns {object} - Schema sanitizado
 */
function sanitizeSchema(schema) {
    if (typeof schema !== 'object' || schema === null) {
        return schema;
    }
    
    const sanitized = JSON.parse(JSON.stringify(schema));
    
    // Función recursiva para limpiar el schema
    function cleanObject(obj) {
        if (typeof obj !== 'object' || obj === null) {
            return obj;
        }
        
        // Remover referencias $data
        if (obj.hasOwnProperty('$data')) {
            console.warn('⚠️ Removiendo $data reference por seguridad (CVE-2025-69873)');
            delete obj.$data;
        }
        
        // Limpiar patrones regex peligrosos en 'pattern' properties
        if (obj.pattern && typeof obj.pattern === 'string') {
            const dangerousPatterns = ['(.*)+', '([.*])*', '(?=.*)+', '(a|a)*', '(.+){'];
            if (dangerousPatterns.some(pattern => obj.pattern.includes(pattern))) {
                console.warn('⚠️ Patrón regex peligroso detectado y removido:', obj.pattern);
                obj.pattern = '^[a-zA-Z0-9]+$'; // Patrón seguro por defecto
            }
        }
        
        // Recursivamente limpiar objetos anidados
        Object.keys(obj).forEach(key => {
            if (typeof obj[key] === 'object' && obj[key] !== null) {
                if (Array.isArray(obj[key])) {
                    obj[key] = obj[key].map(cleanObject);
                } else {
                    cleanObject(obj[key]);
                }
            }
        });
        
        return obj;
    }
    
    return cleanObject(sanitized);
}

/**
 * Middleware para Express.js que intercepta y valida schemas de ajv
 * @param {object} options - Opciones de configuración
 * @returns {function} - Middleware function
 */
function createAjvSecurityMiddleware(options = {}) {
    return function(req, res, next) {
        // Interceptar y validar cualquier schema que se use en la request
        const originalJson = res.json;
        
        res.json = function(data) {
            // Si la respuesta contiene schemas, validarlos
            if (data && typeof data === 'object') {
                const validation = validateSchemaForSecurity(data);
                if (!validation.isValid) {
                    console.warn('🛡️ Schema inseguro detectado en respuesta:', validation.warnings);
                    
                    // Opcional: sanitizar automáticamente
                    if (options.autoSanitize) {
                        data = sanitizeSchema(data);
                        console.log('✅ Schema sanitizado automáticamente');
                    }
                }
            }
            
            return originalJson.call(this, data);
        };
        
        next();
    };
}

// Log de inicialización
console.log('🔒 Mitigación CVE-2025-69873 cargada: ajv $data option security patches');

// Exportar funciones para uso en la aplicación
if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
        AJV_SECURITY_CONFIG,
        createSecureAjv,
        validateSchemaForSecurity,
        sanitizeSchema,
        createAjvSecurityMiddleware
    };
}

// Si se usa como ES6 module en el browser
if (typeof window !== 'undefined') {
    window.AjvSecurity = {
        AJV_SECURITY_CONFIG,
        createSecureAjv,
        validateSchemaForSecurity,
        sanitizeSchema,
        createAjvSecurityMiddleware
    };
}